import { useState, useEffect } from 'react';

import { useAssociations } from '@/hooks/use-data';
import type { Association } from '@/types/models';
import { calculateDistance } from '@/utils/distance';

export interface ClosestAssociation {
	association: Association;
	distance: number;
}

/**
 * Hook to find the closest association to a user's location
 * @param user The user to find closest association for
 * @returns Object containing the closest association and distance, or null if no data available
 */
export function useClosestAssociation({ latitude, longitude }: { latitude?: number, longitude?: number }): ClosestAssociation | null {
	const [closestAssociation, setClosestAssociation] = useState<ClosestAssociation | null>(null);
	const { data: associationsData, error } = useAssociations();

	useEffect(() => {
		// Handle API errors gracefully
		if (error) {
			console.warn('useClosestAssociation: associations data unavailable due to error:', error);
			setClosestAssociation(null);
			return;
		}

		if (!latitude || !longitude) {
			setClosestAssociation(null);
			return;
		}

		// If user doesn't have location, we can't calculate distance
		if (!latitude || !longitude) {
			setClosestAssociation(null);
			return;
		}

		if (!associationsData?.data || !Array.isArray(associationsData.data)) {
			setClosestAssociation(null);
			return;
		}

		// Filter associations that have location data
		const associationsWithLocation = associationsData.data.filter(
			(assoc: Association) => assoc.lat && assoc.lng,
		);

		if (associationsWithLocation.length === 0) {
			setClosestAssociation(null);
			return;
		}

		// Calculate distances and find the closest one
		let closest: ClosestAssociation | null = null;
		let minDistance = Infinity;

		for (const association of associationsWithLocation) {
			const distance = calculateDistance(
				latitude,
				longitude,
				association.lat!,
				association.lng!,
			);

			if (distance < minDistance) {
				minDistance = distance;
				closest = { association, distance };
			}
		}

		setClosestAssociation(closest);
	}, [latitude, longitude, associationsData, error]);

	return closestAssociation;
}
